import { IconButton, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocalStorageState } from '../hooks/useLocalStorageState';

/**
 * Small chevron button for the top-right corner of a dashboard block's header,
 * paired with a `<Collapse>` around the block's body. Use `useSectionCollapse`
 * for the state so the choice survives navigation.
 */
export function SectionCollapseButton({
  expanded,
  onToggle,
  label,
}: {
  expanded: boolean;
  onToggle: () => void;
  /** Block name, used for the tooltip and the accessible label. */
  label: string;
}) {
  const action = expanded ? 'Collapse' : 'Expand';
  return (
    <Tooltip title={`${action} ${label}`}>
      <IconButton
        size="small"
        onClick={onToggle}
        aria-label={`${action} ${label}`}
        aria-expanded={expanded}
        sx={{ p: 0.4, ml: 'auto', flexShrink: 0, color: 'text.secondary' }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        />
      </IconButton>
    </Tooltip>
  );
}

/**
 * Expanded/collapsed state for one dashboard block, expanded by default and
 * remembered per browser (a layout preference, so it isn't scoped to a
 * project). `key` is a stable block name, e.g. "highLevelOverview".
 */
export function useSectionCollapse(key: string): [boolean, () => void] {
  const [expanded, setExpanded] = useLocalStorageState<boolean>(`projectDetail.block.${key}`, true);
  return [expanded, () => setExpanded((v) => !v)];
}
